import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DropDownList, LookupDialogDimensions } from '@core/interfaces';
import { EventService, ListService, fromList } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import { FacilityLookupRequest, FacilityLookupResponseBody } from '#types/facility';

import { AuthService } from '@auth/services';
import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@facility/constants';
import { FacilityLookupDialogComponent } from '@facility/facility-lookup-dialog/facility-lookup-dialog.component';
import { FacilityActions } from '@facility/state';

@Injectable({
  providedIn: 'root',
})
export class FacilityService {
  facilityDictionaryFieldListRequestLoading$ = new Subject<boolean>();
  facilityListsLoading$ = new Subject<boolean>();
  facilityLookupRequestLoading$ = new Subject<boolean>();

  facilityLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  facilityList$ = this.store.select(fromList.selectFacilityList);
  authorizedFacilityList$ = this.store.select(fromList.selectFacilityAuthorizedList);

  facilityDictionaryList$ = (facility: string) => this.store.select(fromList.selectFacilityDictionaryList(facility));

  facilityStatusList$ = this.store.select(fromList.selectFacilityStatusList);

  facilityDropdownList$ = new BehaviorSubject<DropDownList>(null);
  authorizedFacilityDropdownList$ = new BehaviorSubject<DropDownList>(null);
  facilityStatusDropdownList$ = new BehaviorSubject<DropDownList>(null);

  facilityLookupDialogAllowMultiple$ = new BehaviorSubject<boolean>(false);
  facilityLookupDialogSelectionLimit$ = new BehaviorSubject<number>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private listService: ListService,
    private store: Store,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });

    this.authService.facility$.pipe(distinctUntilChanged()).subscribe((facility) => {
      this.requestDictionaryFieldList(facility);
    });
  }

  getFacilityLookup(payload: FacilityLookupRequest) {
    return this.http.post(endpoints.facility.facilityLookup, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_FACILITY_LOOKUP_DIALOG),
      map((response: FacilityLookupResponseBody) => response)
    );
  }

  requestLists() {
    this.store.dispatch(FacilityActions.RequestLists());
  }

  requestDictionaryFieldList(facility: string) {
    if (facility) {
      this.store.dispatch(FacilityActions.RequestFacilityDictionaryFieldList({ facility }));
    }
  }

  initializeDropdownLists() {
    this.facilityList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.facilityDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          header1: this.translate('core.services.list.dropdown.header.facility'),
          header2: this.translate('core.services.list.dropdown.header.name'),
        })
      );
    });

    this.authorizedFacilityList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.authorizedFacilityDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          header1: this.translate('core.services.list.dropdown.header.facility'),
          header2: this.translate('core.services.list.dropdown.header.name'),
        })
      );
    });

    this.facilityStatusList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.facilityStatusDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }

  facilityLookupDialog(
    allowMultipleValues: boolean = false,
    selectionLimit: number = null,
    config: DynamicDialogConfig = {}
  ): DynamicDialogRef {
    this.facilityLookupDialogAllowMultiple$.next(allowMultipleValues);
    this.facilityLookupDialogSelectionLimit$.next(selectionLimit);

    return this.dialogService.open(FacilityLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('facility.facility_lookup_dialog.dialog.title'),
      styleClass: 'lookup-dialog',
      ...config,
    });
  }

  changeFacility() {
    this.authService.chgfacility$.pipe(take(1)).subscribe((chgFacility) => {
      if (chgFacility === 'N') {
        return;
      }

      const lookupDialogRef = this.facilityLookupDialog();

      lookupDialogRef.onClose.pipe(take(1)).subscribe((result) => {
        if (result !== undefined && result.facility) {
          this.store.dispatch(AuthActions.ChangeFacility({ facility: result.facility }));
        }
      });
    });
  }
}
